/**
 * `/$org/workflows`: the organization's workflows on one list, full width
 * (no sidebar beside it), in the Spaces page's frame. Each row shows the workflow's name,
 * how many steps it has and how its last run went; the row opens its chart.
 * New workflow asks for a name and opens the chart of the starter workflow
 * (Start → Prompt → Output). Below, the Browser tasks a Browser task step can
 * run (`BrowserTasks.tsx`).
 *
 * @module components/workflows/WorkflowsPage
 */
import type { WorkflowSummary } from "@amoreg/contracts";
import { type HairlineProps, Relay } from "@lucasmarkes/hairline/react";
import { Link, useNavigate } from "@tanstack/react-router";
import { CalendarClockIcon, ChevronRightIcon, LockIcon, PlusIcon, WorkflowIcon } from "lucide-react";
import { useState, type ComponentType, type ReactNode } from "react";

import { useNowMinute } from "../../hooks/useNowMinute";
import { orgUrlSegment } from "../../orgRouting";
import { useActiveOrg } from "../../state/orgs";
import { createWorkflow, useWorkflows } from "../../state/workflows";
import { ListSkeleton } from "../regulation/LoadingSkeletons";
import { Button } from "../ui/button";
import { Dialog, DialogClose, DialogDescription, DialogFooter, DialogHeader, DialogPanel, DialogPopup, DialogTitle } from "../ui/dialog";
import { HairlineArt } from "../ui/hairline-art";
import { Input } from "../ui/input";
import { ScrollArea } from "../ui/scroll-area";
import { Textarea } from "../ui/textarea";
import { formatRelativeTimeLabel } from "../../timestampFormat";
import { BrowserTasksSection } from "./BrowserTasks";
import { RunStatusPill } from "./workflowParts";

function WorkflowRow({ workflow, org }: { readonly workflow: WorkflowSummary; readonly org: string }) {
  const lastRun = workflow.lastRun;
  return (
    <li className="group relative flex items-center gap-3 rounded-2xl px-3 py-3 transition-colors hover:bg-muted/50">
      <Link
        to="/$org/workflows/$workflowId"
        params={{ org, workflowId: workflow.id }}
        data-workflows-row={workflow.id}
        aria-label={`Open ${workflow.name}`}
        className="absolute inset-0 rounded-2xl outline-none ring-ring focus-visible:ring-2"
      />
      <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted text-muted-foreground">
        <WorkflowIcon className="size-4.5" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 truncate text-sm font-medium text-foreground">
          <span className="truncate">{workflow.name}</span>
          {workflow.lock ? (
            <LockIcon
              className="size-3.5 shrink-0 text-muted-foreground"
              aria-label="Locked"
              data-workflows-row-locked=""
            />
          ) : null}
        </span>
        <p className="truncate text-sm text-muted-foreground">
          {workflow.stepCount === 1 ? "1 step" : `${workflow.stepCount} steps`}
          {workflow.schedule ? (
            <span className={workflow.schedule.lastError ? "text-destructive" : undefined} data-workflows-row-schedule="">
              {" · "}
              <CalendarClockIcon className="inline size-3.5 -translate-y-px" aria-hidden /> {workflow.schedule.description}
            </span>
          ) : null}
          {workflow.description ? <span className="max-sm:hidden"> · {workflow.description}</span> : null}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {lastRun ? (
          <>
            <RunStatusPill status={lastRun.status} />
            <span className="text-xs tabular-nums text-muted-foreground max-sm:hidden">{formatRelativeTimeLabel(lastRun.createdAt)}</span>
          </>
        ) : (
          <span className="text-xs text-muted-foreground">Never run</span>
        )}
        <ChevronRightIcon className="size-4 text-muted-foreground/50 transition-colors group-hover:text-muted-foreground max-sm:hidden" />
      </div>
    </li>
  );
}

function WorkflowsNotice({
  title,
  body,
  action,
  art,
}: {
  readonly title: string;
  readonly body: string;
  readonly action?: ReactNode;
  readonly art?: ComponentType<HairlineProps>;
}) {
  return (
    <div
      className="mt-6 flex flex-col items-center rounded-xl border border-dashed border-border/60 px-6 py-16 text-center"
      data-workflows-empty=""
    >
      {art ? (
        <HairlineArt figure={art} className="mb-1 w-48 sm:w-56" />
      ) : (
        <WorkflowIcon className="size-5 text-muted-foreground/60" />
      )}
      <p className="mt-3 text-sm font-medium text-foreground">{title}</p>
      <p className="mt-1 max-w-sm text-sm text-muted-foreground">{body}</p>
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}

/** Asks for a name, creates the workflow and opens its chart. */
export function NewWorkflowDialog({
  organizationId,
  spaceId,
  open,
  onOpenChange,
}: {
  readonly organizationId: string;
  readonly spaceId?: string | null;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const create = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    const outcome = await createWorkflow(organizationId, {
      name: name.trim(),
      ...(description.trim() ? { description: description.trim() } : {}),
      ...(spaceId ? { spaceId } : {}),
    });
    setBusy(false);
    if (!outcome.ok) {
      setError(outcome.message);
      return;
    }
    onOpenChange(false);
    setName("");
    setDescription("");
    void navigate({
      to: "/$org/workflows/$workflowId",
      params: { org: orgUrlSegment(organizationId), workflowId: outcome.value.id },
    });
  };
  return (
    <Dialog open={open} onOpenChange={(next) => (!busy ? onOpenChange(next) : undefined)}>
      <DialogPopup className="max-w-md">
        <DialogHeader>
          <DialogTitle>New workflow</DialogTitle>
          <DialogDescription>
            It starts as Start → Prompt → Output. Add steps and link them on the chart.
          </DialogDescription>
        </DialogHeader>
        <DialogPanel className="space-y-3">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">Name</span>
            <Input
              autoFocus
              value={name}
              onChange={(event) => setName(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void create();
              }}
              placeholder="Quarterly client check"
              maxLength={200}
            />
          </label>
          <label className="block space-y-1.5">
            <span className="text-xs font-medium">Description (optional)</span>
            <Textarea
              value={description}
              onChange={(event) => setDescription(event.currentTarget.value)}
              placeholder="What it is for, and when to run it"
              rows={2}
              maxLength={2000}
            />
          </label>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
        </DialogPanel>
        <DialogFooter>
          <DialogClose render={<Button size="sm" variant="ghost" disabled={busy}>Cancel</Button>} />
          <Button size="sm" onClick={() => void create()} disabled={busy || !name.trim()} data-workflows-create="">
            {busy ? "Creating…" : "Create workflow"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
}

export function WorkflowsPage() {
  const activeOrg = useActiveOrg();
  const organizationId = activeOrg?.id ?? null;
  const { read } = useWorkflows(organizationId);
  const [creating, setCreating] = useState(false);
  // Re-renders each minute, so "5m ago" stays true.
  useNowMinute();
  const org = organizationId ? orgUrlSegment(organizationId) : null;

  const newButton = (
    <Button size="sm" className="cursor-pointer rounded-full" onClick={() => setCreating(true)} data-workflows-new="">
      <PlusIcon />
      New workflow
    </Button>
  );

  return (
    // The page's own surface, as Home and Spaces paint theirs: without it the
    // shell's card shows through, a darker step in the default dark theme.
    <ScrollArea className="h-full bg-background text-foreground">
      <div className="mx-auto w-full max-w-4xl px-4 pb-16 pt-14 sm:px-6 sm:pt-8" data-workflows-page="">
        <header className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
          <div className="min-w-0">
            <h1 className="text-[22px] font-semibold tracking-[-0.01em] text-foreground">Workflows</h1>
            <p className="mt-1 max-w-xl text-[13px] text-muted-foreground">
              {activeOrg
                ? `Repeatable work for ${activeOrg.name}, drawn as a chart of steps. Run one and watch each step finish.`
                : "Repeatable work, drawn as a chart of steps. Run one and watch each step finish."}
            </p>
          </div>
          {org && read?.kind === "ready" ? newButton : null}
        </header>

        {read === null ? (
          <div className="mt-6">
            <ListSkeleton />
          </div>
        ) : read.kind === "unavailable" ? (
          <WorkflowsNotice title="Workflows could not be loaded" body="Something went wrong on our side. Reload the page to try again." />
        ) : read.kind === "missing" ? (
          <WorkflowsNotice title="Workflows are not available here" body="Choose an organization to see its workflows." />
        ) : read.value.length === 0 ? (
          <WorkflowsNotice
            art={Relay}
            title="No workflows yet"
            body="A workflow is a chart of steps: a prompt over what you give it, then another, then the result. Make one for work you repeat."
            action={org ? newButton : null}
          />
        ) : (
          <ul className="mt-6 space-y-1" data-workflows-list="">
            {read.value.map((workflow) => (
              <WorkflowRow key={workflow.id} workflow={workflow} org={org ?? ""} />
            ))}
          </ul>
        )}
        {organizationId && read?.kind !== "missing" ? <BrowserTasksSection organizationId={organizationId} /> : null}
      </div>
      {organizationId ? <NewWorkflowDialog organizationId={organizationId} open={creating} onOpenChange={setCreating} /> : null}
    </ScrollArea>
  );
}
